import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem } from '@/types';

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, cantidad: number) => void;
  clearCart: () => void;
  toggleCart: (open?: boolean) => void;
  subtotal: () => number;
  itemCount: () => number;
}

function cartItemId(
  tipo: 'producto' | 'kit',
  refId: number,
  origen?: string,
  idExterno?: string | null,
): string {
  if (origen && origen !== 'alarcom' && idExterno) {
    return `${tipo}-${origen}-${idExterno}`;
  }
  return `${tipo}-${origen ?? 'alarcom'}-${refId}`;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addItem: (item) => {
        const origen = item.origen ?? 'alarcom';
        const id = cartItemId(item.tipo, item.refId, origen, item.idExterno);
        const existing = get().items.find((i) => i.id === id);

        if (existing) {
          set({
            items: get().items.map((i) =>
              i.id === id ? { ...i, cantidad: i.cantidad + item.cantidad } : i,
            ),
            isOpen: true,
          });
          return;
        }

        set({
          items: [
            ...get().items,
            { ...item, id, origen, idExterno: item.idExterno ?? null, skuExterno: item.skuExterno ?? null },
          ],
          isOpen: true,
        });
      },

      removeItem: (id) => {
        set({ items: get().items.filter((i) => i.id !== id) });
      },

      updateQuantity: (id, cantidad) => {
        if (cantidad < 1) {
          get().removeItem(id);
          return;
        }
        set({
          items: get().items.map((i) => (i.id === id ? { ...i, cantidad } : i)),
        });
      },

      clearCart: () => set({ items: [] }),

      toggleCart: (open) =>
        set({ isOpen: open !== undefined ? open : !get().isOpen }),

      subtotal: () =>
        get().items.reduce((acc, i) => acc + i.precio * i.cantidad, 0),

      itemCount: () =>
        get().items.reduce((acc, i) => acc + i.cantidad, 0),
    }),
    { name: 'alarcom-cart' },
  ),
);
